<template>
  <div class="col-lg-8 mx-auto">
    <header class="text-center">
      <workflow-navigation workflowStage="DB Options" />
      <div class="col-lg-10 mx-auto">
          <h2 class="skin-page-heading">
            Your options in the Aon pension scheme
          </h2>
          <p>
            You can see the different options available below from your planned retirement date of 1 September 2025.
            You can select these to see how they affect your pension and tax free cash.
          </p>
      </div>
    </header>

    <SubSection title="Your scheme choices" :show-help="false">
        <h3>Tax free cash</h3>

        <p>
            Your first decision is to decide whether to choose betwen a full scheme pension or a lower scheme pension with a tax free cash lump sum.
            <help title="Tax free cash" :text="HelpText.TextNotYetDefined()" />
        </p>
        <p>Use the slider to choose between zero tax free cash and the maximum possible</p>

        <div class="col-md-8 text-start d-flex flex-wrap align-items-center">
            <span class="me-4 text-nowrap">
                Tax free cash:
                <span class="fw-bold" style="display: inline-block; width: 2ch; text-align: right;">
                    {{ formatter.formatCurrency(taxFreeCashModel) }}
                </span>
            </span>
            <div class="d-flex flex-grow-1 align-items-center mt-2 mt-md-0">
                <span class="ms-4 me-3">0</span>
                <input
                    type="range"
                    class="form-range my-3 flex-grow-1"
                    v-model="taxFreeCashModel"
                    min="0"
                    :max="taxFreeCashMax"
                    step="100"
                />
                <span class="ms-3 me-2">{{ formatter.formatCurrency(taxFreeCashMax) }}</span>
                <help title="Tax free cash maximum" :text="HelpText.TextNotYetDefined()" />
            </div>
        </div>


        <h3 class="mt-5">Your scheme pension tranches</h3>

        <p>Your scheme pension is made up from the following tranches.</p>

        <div class="table-responsive mb-2">
            <table class="table table-striped align-middle">
                <thead>
                    <tr>
                        <th>Pension type</th>
                        <th class="numeric">Starting amount</th>
                        <th>Increase type</th>
                        <th class="numeric">Min. rate</th>
                        <th class="numeric">Max. rate</th>
                    </tr>
                </thead>
                <tbody>
                    <tr v-for="(tranche, i) in modifiedSchemeTranches" v-bind:key="i">
                        <td>{{ getPensionTrancheTypeDescription(tranche.pensionType) }}</td>
                        <td class="numeric">{{ formatter.formatCurrency(tranche.startingAmount) }}</td>
                        <td>{{ getIncreaseTypeDescription(tranche.increaseType) }}</td>
                        <td class="numeric">{{ tranche.minRate }}</td>
                        <td class="numeric">{{ tranche.maxRate }}</td>
                    </tr>
                </tbody>
            </table>
        </div>


        <h3 class="mt-5">Your other options</h3>
        <p>In addition to this cash option the AON Pension scheme also offers two other options:</p>
        <ul>
            <li>
                <p>
                    <span class="fw-bold">A Pension Increase Exchange (PIE) option</span> - You can exchange some or all of your pension
                    increases for a higher starting pension with lower or zero increases.
                    <help title="Pension Increase Exchange" :text="HelpText.TextNotYetDefined()" />
                </p>
            </li>
            <li>
                <p>
                    <span class="fw-bold">A Bridging Pension (BP) option</span> - You can exchange some of your lifetime pension for a
                    a larger temporary pension until State Pension age.
                    <help title="Bridging Pension" :text="HelpText.TextNotYetDefined()" />
                </p>
            </li>
        </ul>
        <p>Select the PIE or BP option to examine these scenarios and compare them to the scheme pension in the chart below.</p>
        <options-question
            name="schemeOption"
            question=""
            :options="[pensionIncreaseExchangeName, bridgingPensionName]"
            v-model:selected="optionModel" />

        <div class="table-responsive mt-4 mb-2">
            <table class="table table-striped align-middle">
                <thead>
                <tr>
                    <th>Pension type</th>
                    <th class="numeric">Starting amount</th>
                    <th>Increase type</th>
                    <th class="numeric">Min. rate</th>
                    <th class="numeric">Max. rate</th>
                </tr>
                </thead>
                <tbody>
                <tr v-for="(tranche, i) in optionTableTranches" v-bind:key="i">
                    <td>{{ getPensionTrancheTypeDescription(tranche.pensionType) }}</td>
                    <td class="numeric">{{ formatter.formatCurrency(tranche.startingAmount) }}</td>
                    <td>{{ getIncreaseTypeDescription(tranche.increaseType) }}</td>
                    <td class="numeric">{{ tranche.minRate }}</td>
                    <td class="numeric">{{ tranche.maxRate }}</td>
                </tr>
                </tbody>
            </table>
        </div>


        <h3 class="mt-5">Your income from your pension</h3>
        <p>The expected annual pension payments are shown in the chart below and compared to the scheme option.</p>
        <p>In addition important information in making your decision such as your average life expectancy, crossover age and breakeven age are also shown.</p>

        <DBOptionsChart :graph-data="incomeGraphData"></DBOptionsChart>

        <p>Crossover age: {{ crossoverAge }}, Breakeven age: {{ breakevenAge }}, Life expectancy: 88</p>

        <h4>Inflation</h4>
        <p>
            Some of your scheme pension may be linked to inflation in which case the chart and average increase rates of the
            scheme pension will change depending on what you assume for inflation. If you have selected the PIE option above the 
            average increase under this option is also shown.
            <help title="Inflation" :text="HelpText.TextNotYetDefined()" />
        </p>

        <div class="row">
            <div class="col-md-7 text-start d-flex flex-wrap align-items-center">
                <p>Use the slider to explore how different levels of inflation could impact your retirement income.</p>
                <span class="me-4 text-nowrap">
                    Future inflation:
                    <span class="fw-bold" style="display: inline-block; width: 2ch; text-align: right;">
                        {{ futureInflationModel }}%
                    </span>
                </span>
                <div class="d-flex flex-grow-1 align-items-center mt-2 mt-md-0">
                    <span class="ms-4 me-3">0</span>
                    <input
                        type="range"
                        class="form-range my-3 flex-grow-1"
                        v-model="futureInflationModel"
                        min="0"
                        max="5"
                        step="0.1"
                    />
                    <span class="ms-3">5%</span>
                </div>
            </div>

            <div class="col-md-5">
                <div class="ps-md-4 border-start">
                    <p class="mb-3 fw-bold">Average increases</p>
                    <p class="mb-1">Scheme pension: <span class="fw-bold">{{ averageSchemeIncreases.toFixed(2) }}%</span></p>
                    <p v-show="showPIEOption" class="mb-0">PIE option: <span class="fw-bold">{{ averagePIEIncreases.toFixed(2) }}%</span></p>
                    <!-- <p v-show="!showPIEOption" class="mb-0">BPO option: <span class="fw-bold">{{ averageBPOIncreases.toFixed(2) }}%</span></p> -->
                </div>
            </div>
        </div>


        <h3 class="mt-5">Your decision</h3>
        <options-question
            name="pensionChoice"
            question="Do you want to stay with the scheme pension, or take the PIE or BPO option?"
            :options="['Scheme pension', pensionIncreaseExchangeName, bridgingPensionName]"
            v-model:selected="pensionChoiceModel" />


        <h3 class="mt-5">Modelling your Aon pension scheme choices</h3>
        <p>
            Once you have selected all your options just click next and the income and any lump sums will be shown
            as assets on the next page. You can always come back and change the option selected and re-model before you make a
            final decision.
        </p>

        <h4>Tax free cash</h4>
        <template v-if="!isTakingTaxFreeCash">
            <p>You have chosen to not take any tax free cash.</p>
        </template>
        <template v-else>
            <p>
                You can chose how to model your tax free cash. A new savings asset will be created within the modeller for any
                amount that you don't intend to spend immediately.
            </p>

            <div class="col-md-7 text-start d-flex flex-wrap align-items-center">
                <span class="me-4 text-nowrap">
                    Immediate spend:
                    <span class="fw-bold" style="display: inline-block; width: 2ch; text-align: right;">
                        £{{ immediateSpendModel }}
                    </span>
                </span>
                <div class="d-flex flex-grow-1 align-items-center mt-2 mt-md-0">
                    <span class="ms-4 me-3">0</span>
                    <input
                        type="range"
                        class="form-range my-3 flex-grow-1"
                        v-model="immediateSpendModel"
                        min="0"
                        :max="taxFreeCashModel"
                        step="100"
                    />
                    <span class="ms-3">{{ formatter.formatCurrency(taxFreeCashModel) }}</span>
                </div>
            </div>
        </template>


        <p v-if="errorMessage.length > 0" class="text-danger mt-5" style="color: red !important">
            <i class="bi bi-x-circle-fill"></i>
            {{ errorMessage }}
        </p>
    </SubSection>

    <page-nav back-link="requirements" @next-clicked="onNextClicked" :parent-will-handle-next="true" next-link="haves" />
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, watch } from "vue";
import WorkflowNavigation from "@/guiide/components/common/WorkflowNavigation.vue";
import HelpText from "@/guiide/ts/helpText";
import Help from "@/guiide/components/common/Help.vue";
import PageNav from "@/guiide/components/common/PageNav.vue";
import SubSection from "@/guiide/components/common/SubSection.vue";
import OptionsQuestion from "./common/OptionsQuestion.vue";
import DBOptionsChart from "./charts/DBOptionsChart.vue";
import { workflow } from "../ts/workflow/workflow";
import View from "../ts/view";
import { formatter } from "../../shared/ts/formatter";
import { ChartData, X, Y } from "../ts/charts/chartDataPoint";

enum IncreaseType {
    InflationLinked,
    Fixed,
    NonIncreasing
}

enum PensionTrancheType {
    Lifetime,
    ToStatePensionAge
}

type Tranche = { pensionType: PensionTrancheType; startingAmount: number, increaseType: IncreaseType, minRate: number, maxRate: number };
type TrancheIncreaseInfo = { amount: number, increaseRate: number };
type SchemeIncreaseInfo = { increaseRate: number, increasePercentage: number, startingAmount: number, trancheIncreaseInfo: Array<TrancheIncreaseInfo> };

const schemeTranches: Tranche[] = [
    { pensionType: PensionTrancheType.Lifetime, startingAmount: 10000, increaseType: IncreaseType.InflationLinked, minRate: 0, maxRate: 5 },
    { pensionType: PensionTrancheType.Lifetime, startingAmount: 5000, increaseType: IncreaseType.InflationLinked, minRate: 0, maxRate: 5 },
    { pensionType: PensionTrancheType.Lifetime, startingAmount: 3000, increaseType: IncreaseType.Fixed, minRate: 3, maxRate: 3 }
];
const pieTranches: Tranche[] = [
    { pensionType: PensionTrancheType.Lifetime, startingAmount: 12000, increaseType: IncreaseType.InflationLinked, minRate: 0, maxRate: 1 },
    { pensionType: PensionTrancheType.Lifetime, startingAmount: 6000, increaseType: IncreaseType.InflationLinked, minRate: 0, maxRate: 0 },
    { pensionType: PensionTrancheType.Lifetime, startingAmount: 3000, increaseType: IncreaseType.Fixed, minRate: 3, maxRate: 3 }
];
const bpoTranches: Tranche[] = [
    { pensionType: PensionTrancheType.Lifetime, startingAmount: 8500, increaseType: IncreaseType.InflationLinked, minRate: 0, maxRate: 5 },
    { pensionType: PensionTrancheType.Lifetime, startingAmount: 2000, increaseType: IncreaseType.InflationLinked, minRate: 0, maxRate: 5 },
    { pensionType: PensionTrancheType.Lifetime, startingAmount: 1000, increaseType: IncreaseType.Fixed, minRate: 3, maxRate: 3 },
    { pensionType: PensionTrancheType.ToStatePensionAge, startingAmount: 13000, increaseType: IncreaseType.NonIncreasing, minRate: 0, maxRate: 0 },
];

const commutationFactor = 15;
const pensionIncreaseExchangeName = 'Pension Increase Exchange', bridgingPensionName = 'Bridging Pension';
const ageChartDataIndex = X, amountChartDataIndex = Y;

const modifiedSchemeTranches = ref<Array<Tranche>>(new Array<Tranche>());
const modifiedPIETranches = ref<Array<Tranche>>(new Array<Tranche>());
const modifiedBPOTranches = ref<Array<Tranche>>(new Array<Tranche>());
const nextLink = ref("");
const taxFreeCashMax = ref(50000);
const taxFreeCashModel = ref(0);
const optionModel = ref(pensionIncreaseExchangeName);
const crossoverAge = ref("");
const breakevenAge = ref("");
const futureInflationModel = ref(2.5);
const averageSchemeIncreases = ref(2.5);
const averagePIEIncreases = ref(2.5);
const averageBPOIncreases = ref(2.5);
const immediateSpendModel = ref(0);
const pensionChoiceModel = ref("");
const errorMessage = ref("");

onMounted(async () => {
    initialiseModifiableTranches(schemeTranches, modifiedSchemeTranches.value);
    initialiseModifiableTranches(pieTranches, modifiedPIETranches.value);
    initialiseModifiableTranches(bpoTranches, modifiedBPOTranches.value);

    const nextWorkflowStep = await workflow.getNextWorkflowStep(View.DBOptions);
    nextLink.value = nextWorkflowStep.resource;
});

watch(
    () => taxFreeCashModel.value,
    (newTaxFreeCash: number) => {
        const pensionSacrifce = newTaxFreeCash / commutationFactor;

        modifyTranchesForPensionSacrifice(schemeTranches, modifiedSchemeTranches.value, pensionSacrifce);
        modifyTranchesForPensionSacrifice(pieTranches, modifiedPIETranches.value, pensionSacrifce);
        modifyTranchesForPensionSacrifice(bpoTranches, modifiedBPOTranches.value, pensionSacrifce);
    }
)

const showPIEOption = computed<boolean>(() => optionModel.value == pensionIncreaseExchangeName);

const optionTableTranches = computed<Array<Tranche>>(() => {
    return showPIEOption.value
        ? modifiedPIETranches.value
        : modifiedBPOTranches.value;
})

const incomeGraphData = computed(() => {
    const schemeGraphData = createSchemeGraphSeries();
    const optionGraphData = showPIEOption.value
        ? createPIEGraphSeries()
        : createBPOGraphSeries();

    const crossoverAndBreakeven = identifyCrossoverAndBreakeven(schemeGraphData.data, optionGraphData.data);
    crossoverAge.value = crossoverAndBreakeven.crossover == null ? "n/a" : crossoverAndBreakeven.crossover.toString();
    breakevenAge.value = crossoverAndBreakeven.breakeven == null ? "n/a" : crossoverAndBreakeven.breakeven.toString();

    const data = [
        schemeGraphData,
        optionGraphData
    ];
    
    return data;
});

const isTakingTaxFreeCash = computed<boolean>(() => taxFreeCashModel.value > 0);

async function onNextClicked() {
    if (pensionChoiceModel.value == "") {
        errorMessage.value = "Please indicate whether you want to remain with the scheme, or chose the PIE or BPO option.";
        return;
    }

    await workflow.goToNextWorkflowStep();
}

function createPIEGraphSeries() {
    const schemeIncreaseInfo = calculateSchemeIncreaseInfo(modifiedPIETranches.value);
    averagePIEIncreases.value = schemeIncreaseInfo.increasePercentage;
    const income = getPensionIncome(schemeIncreaseInfo.startingAmount, schemeIncreaseInfo.increaseRate);
    return createGraphSeries(income, 'PIE option payments', 'skyblue');
}

function createBPOGraphSeries() {
    const schemeIncreaseInfo = calculateSchemeIncreaseInfo(modifiedBPOTranches.value);
    averageBPOIncreases.value = schemeIncreaseInfo.increasePercentage;
    const income = getPensionIncome(schemeIncreaseInfo.startingAmount, schemeIncreaseInfo.increaseRate);
    return createGraphSeries(income, 'BPO option payments', '#e0b592');
}

function createSchemeGraphSeries() {
    const schemeIncreaseInfo = calculateSchemeIncreaseInfo(modifiedSchemeTranches.value);
    averageSchemeIncreases.value = schemeIncreaseInfo.increasePercentage;
    const income = getPensionIncome(schemeIncreaseInfo.startingAmount, schemeIncreaseInfo.increaseRate);
    return createGraphSeries(income, 'Scheme payments', '#ACC0C4');
}

function calculateSchemeIncreaseInfo(tranches: Array<Tranche>): SchemeIncreaseInfo {
    const trancheIncreaseInfo = new Array<TrancheIncreaseInfo>();

    for (const schemeTranche of tranches) {
        if (schemeTranche.startingAmount == 0) {
            continue;
        }

        let adjustmentRate = 1;

        if (schemeTranche.increaseType != IncreaseType.NonIncreasing) {
            const adjustmentPercentage = schemeTranche.increaseType == IncreaseType.Fixed
                ? schemeTranche.minRate
                : Math.min(futureInflationModel.value, schemeTranche.maxRate);

            adjustmentRate = 1 + (adjustmentPercentage / 100);
        }

        trancheIncreaseInfo.push({ amount: schemeTranche.startingAmount, increaseRate: adjustmentRate });
    }

    const totalPayments = trancheIncreaseInfo.reduce((sum, tii) => sum + tii.amount, 0);
    const weightedSum = trancheIncreaseInfo.reduce((sum, tii) => sum + (tii.amount * tii.increaseRate), 0);
    const schemeIncreaseRate = weightedSum / totalPayments;
    const increasePercentage = (schemeIncreaseRate - 1) * 100;

    return { increaseRate: schemeIncreaseRate, increasePercentage, startingAmount: totalPayments, trancheIncreaseInfo };
}

function getPensionIncome(startingAmount: number, increaseRate: number): ChartData {
    const income: ChartData = [];

    for (let retirementYear = 0; retirementYear < 24; retirementYear++) {
        const age = retirementYear + 65;
        const amountInYear = startingAmount * Math.pow(increaseRate, retirementYear);
        income.push([age, Math.round(amountInYear)]);
    }

    return income;
}

function identifyCrossoverAndBreakeven(schemeIncome: ChartData, optionIncome: ChartData): { crossover: number|null, breakeven: number|null } {
    let crossover: number|null = null;
    let breakeven: number|null = null;
    let accumulator = 0;
    let mode: "accum"|"decum" = "accum";

    for (let retirementYear = 0; retirementYear < schemeIncome.length; retirementYear++) {
        const schemeIncomePoint = schemeIncome[retirementYear];
        const optionIncomePoint = optionIncome[retirementYear];

        const diff = Math.abs(schemeIncomePoint[amountChartDataIndex] - optionIncomePoint[amountChartDataIndex]);
        if (mode == "accum") {
            accumulator += diff;
        } else {
            accumulator -= diff;

            if (accumulator <= 0) {
                breakeven = schemeIncomePoint[ageChartDataIndex];
                return { crossover, breakeven };
            }
        }

        if (schemeIncomePoint[amountChartDataIndex] > optionIncomePoint[amountChartDataIndex]) {
            crossover = schemeIncomePoint[ageChartDataIndex];
            mode = "decum";
        }
    }

    return { crossover, breakeven };
}

function createGraphSeries(data: ChartData, name: string, color: string) {
    return {
        "name": name,
        "data": data,
        "visible": true,
        "zIndex": 10,
        "legendIndex": 0,
        "color": color,
        "type": "line",
        "lineWidth": 3,
        "marker": {
            "enabled": false
        }
    }
}

function getIncreaseTypeDescription(increaseType: IncreaseType): string {
    switch (increaseType) {
        case IncreaseType.Fixed:
            return "Fixed";
        case IncreaseType.InflationLinked:
            return "Linked to inflation";
        case IncreaseType.NonIncreasing:
            return "Non increasing";
        default:
            throw new Error("Increase type not handled");
    }
}

function getPensionTrancheTypeDescription(pensionTrancheType: PensionTrancheType): string {
    switch (pensionTrancheType) {
        case PensionTrancheType.Lifetime:
            return "Lifetime";
        case PensionTrancheType.ToStatePensionAge:
            return "To State Pension age";
        default:
            throw new Error("PensionTrancheType not handled");
    }
}

function modifyTranchesForPensionSacrifice(source: Array<Tranche>, target: Array<Tranche>, totalSacrifice: number) {
    const trancheReduction = Math.round(totalSacrifice / source.length);

    let i = 0;
    for (const tranche of source) {
        const reducedAmount = tranche.startingAmount - trancheReduction;
        target[i].startingAmount = Math.max(0, reducedAmount);
        i++;
    }
}

function initialiseModifiableTranches(source: Array<Tranche>, target: Array<Tranche>) {
    for (const sourceTranche of source) {
        const clone = cloneTranche(sourceTranche);
        target.push(clone);
    }
}

function cloneTranche(tranche: Tranche): Tranche {
    return {
        pensionType: tranche.pensionType,
        startingAmount: tranche.startingAmount,
        increaseType: tranche.increaseType,
        minRate: tranche.minRate,
        maxRate: tranche.maxRate
    };
}
</script>

<style scoped lang="css">
.form-check-input {
  width: 0;
  height: 0;
  opacity: 0;
  vertical-align: middle;
}

th.numeric {
  text-align: right;
}

td.numeric {
  text-align: right;
}
</style>
